import { useEffect, useRef, useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import {
  lineItemAffordanceStyle,
  lineItemInputStyle,
  type LineItemAlign
} from '@/document-render/presentation/blocks/lineItems/lineItemsQuoteStyles';

const stop = (event: SyntheticEvent) => event.stopPropagation();

export function EditableLineCell({
  label,
  value,
  editable,
  emptyMarker,
  displayText,
  align = 'left',
  onCommit
}: {
  label: string;
  value: string;
  editable: boolean;
  emptyMarker?: 'dash' | 'add';
  /** What a read-only cell prints instead of the raw value (e.g. a currency-formatted rate). */
  displayText?: string;
  /** Text alignment of the cell's content; numeric columns read right-aligned. */
  align?: LineItemAlign;
  onCommit: (value: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  // The text being typed; null means "not editing", so the input shows the model's `value`. A rejected
  // edit (or an undo/redo that changes `value`) therefore never leaves stale text in the cell. The ref
  // mirrors the state so Enter -> blur cannot commit twice before React re-renders.
  const [draft, setDraftState] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setDraft = (next: string | null) => {
    draftRef.current = next;
    setDraftState(next);
  };
  // Opening an empty cell moves focus into it (the seller just asked to type there).
  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  const commit = () => {
    const next = draftRef.current;
    setDraft(null);
    if (!value) setAdding(false);
    if (next === null || next === value) return;
    // An empty cell the seller left blank is not an edit.
    if (!value && next.trim() === '') return;
    onCommit(next);
  };

  const cancel = () => {
    setDraft(null);
    setAdding(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      commit();
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      cancel();
      event.currentTarget.blur();
    }
  };

  if (!editable) {
    return <span>{displayText || value || '—'}</span>;
  }
  if (!value && emptyMarker === 'dash') {
    return (
      <span data-empty-quantity="true" style={{ color: 'var(--mui-palette-error-main)' }}>
        {'—'}
      </span>
    );
  }
  if (!value && !adding) {
    // "+ Add" only opens the cell. Nothing is written until the seller types something.
    return (
      <button
        type="button"
        aria-label={label}
        data-canvas-affordance="pencil"
        style={lineItemAffordanceStyle(align)}
        onClick={() => setAdding(true)}
      >
        {emptyMarker === 'add' ? '+ Add …' : 'Edit'}
      </button>
    );
  }
  return (
    <input
      aria-label={label}
      ref={inputRef}
      value={draft ?? value}
      style={lineItemInputStyle(align)}
      onFocus={() => setDraft(value)}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={onKeyDown}
      onBlur={commit}
      onClick={stop}
      onDoubleClick={stop}
    />
  );
}
